import events from '~/modules/game-events/game-events';
import { ChannelName } from '~/modules/remote-mic/network/client/subscriptions';
import { NetworkMessages } from '~/modules/remote-mic/network/messages';
import { SenderInterface } from '~/modules/remote-mic/network/server/transport/interface';
import { RemoteMic } from '~/modules/remote-mic/remote-mic-input';
import Listener from '~/modules/utils/listener';
import storage from '~/modules/utils/storage';
import { DefaultRemoteMicPermission, RemoteMicPermission } from '~/routes/settings/settings-state';

const RememberedAccessesKey = 'RememberedAccessesKey';
const RememberedSubscriptionsKey = 'rememberedSubscriptionsKey';
type subscriptionChannels = ChannelName;

/** How long a phone can be away before the seat it held stops being kept for it. */
const LONG_GONE_MS = 5 * 60_000;

class RemoteMicManager extends Listener<[string, RemoteMicPermission]> {
  private remoteMics: RemoteMic[] = [];
  /** When each phone that left was last here. One never seen this session counts from start-up. */
  private goneSince = new Map<string, number>();
  private readonly startedAt = Date.now();
  private micAccessMap: Record<string, RemoteMicPermission> = storage.local?.getItem(RememberedAccessesKey) ?? {};

  private subscriptions: Record<subscriptionChannels, string[]> = {
    'remote-mics': [],
    'keyboard-layout': [],
    'song-selection': [],
    'played-songs': [],
    singers: [],
    'current-song': [],
    'song-queue': [],
    style: [],
    ...storage.session?.getItem(RememberedSubscriptionsKey),
  };

  public constructor() {
    super();

    // Nothing a mic sends can reach the game while the game is off the relay, so the phones are not
    // asked anything in the meantime - silence during the game's own outage says nothing about them.
    events.micServerStopped.subscribe(() => this.remoteMics.forEach((remoteMic) => remoteMic.pause()));
    events.micServerStarted.subscribe(() => this.remoteMics.forEach((remoteMic) => remoteMic.resume()));
  }

  public addRemoteMic = (
    id: string,
    name: string,
    connection: SenderInterface,
    silent: boolean,
    lag: number,
    resumed = false,
  ) => {
    const known = this.getRemoteMicById(id) !== undefined;
    this.goneSince.delete(id);
    // A phone that re-registers replaces the entry it had before - and the one being replaced has
    // to be told, or its ping loop keeps running against a connection nobody reads any more.
    this.getRemoteMicById(id)?.onDisconnect();
    this.remoteMics = this.remoteMics.filter((remoteMic) => remoteMic.id !== id);
    // One singer is one phone. A second phone arriving under a name somebody is already singing as
    // would split that round's scores between two entries of the same person, so it joins under a
    // free variation of the name instead of being refused outright - a phone that cannot join is
    // worse than a phone joining as "Mia (2)", and the name is editable the moment it is connected.
    const claimed = this.freeSingerName(name, id);
    this.remoteMics = [...this.remoteMics, new RemoteMic(id, claimed, connection, lag, this.handleSilentMic)];
    this.setPermission(id, this.getPermission(id));

    // A phone resuming a connection this game still has on its list is a blip the room does not
    // need to hear about. One the game has never seen is an arrival - which is what a phone coming
    // back to a game that restarted is, from the room's point of view.
    events.remoteMicConnected.dispatch({ id, name: claimed, silent: silent || (resumed && known) });
  };

  /**
   * A phone that has not answered a ping in fifteen seconds is gone as far as the room is concerned:
   * the seat it held is freed, the song stops waiting on it to confirm somebody is holding it, and
   * the room is told. If it comes back it re-registers, and `rememberSlot` puts it where it was.
   */
  private handleSilentMic = (id: string) => {
    this.removeRemoteMic(id);
  };

  /** The mic already connected under this name, ignoring the one asking. Names match case-blind. */
  public getMicHoldingName = (name: string, exceptId?: string) => {
    const wanted = name.trim().toLowerCase();
    if (!wanted) return undefined;

    return this.remoteMics.find((mic) => mic.id !== exceptId && mic.name.trim().toLowerCase() === wanted);
  };

  /** The name as asked for, or the first numbered variation of it nobody else is holding. */
  public freeSingerName = (name: string, exceptId?: string) => {
    if (!this.getMicHoldingName(name, exceptId)) return name;

    for (let suffix = 2; suffix < 100; suffix++) {
      const candidate = `${name} (${suffix})`;
      if (!this.getMicHoldingName(candidate, exceptId)) return candidate;
    }
    return name;
  };

  public removeRemoteMic = (id: string, silent = false) => {
    const remoteMic = this.remoteMics.find((remoteMic) => remoteMic.id === id);
    this.remoteMics = this.remoteMics.filter((remoteMic) => remoteMic.id !== id);
    if (remoteMic) {
      this.goneSince.set(id, Date.now());
      remoteMic.onDisconnect();
      events.remoteMicDisconnected.dispatch(remoteMic, silent);
    }
  };

  public getRemoteMics = () => this.remoteMics;

  /** Away long enough that a seat held for it is a singer who has gone home, not one reconnecting. */
  public isLongGone = (id: string | undefined, now = Date.now()) => {
    if (!id || this.getRemoteMicById(id)) return false;

    return now - (this.goneSince.get(id) ?? this.startedAt) > LONG_GONE_MS;
  };

  public getRemoteMicById = (id: string) => this.remoteMics.find((remoteMic) => remoteMic.id === id);

  public broadcast = (event: NetworkMessages) => {
    this.getRemoteMics().forEach((remoteMic) => remoteMic.connection.send(event));
  };

  public broadcastToChannel = (channel: subscriptionChannels, event: NetworkMessages) => {
    this.subscriptions[channel].forEach((id) => this.getRemoteMicById(id)?.connection.send(event));
  };

  public getPermission = (id: string) => this.micAccessMap[id] ?? DefaultRemoteMicPermission.get();

  public setPermission = (id: string, permission: RemoteMicPermission) => {
    this.getRemoteMicById(id)?.setPermission(permission);

    this.micAccessMap[id] = permission;

    storage?.setItem(RememberedAccessesKey, this.micAccessMap);
    this.onUpdate(id, permission);
  };

  public addSubscription = (id: string, channel: subscriptionChannels) => {
    this.subscriptions[channel] = [...new Set([...this.subscriptions[channel], id])];
    storage.session?.setItem(RememberedSubscriptionsKey, this.subscriptions);
  };

  public removeSubscription = (id: string, channel: subscriptionChannels) => {
    this.subscriptions[channel] = this.subscriptions[channel]?.filter((remoteMicId) => remoteMicId !== id) ?? [];
    storage.session?.setItem(RememberedSubscriptionsKey, this.subscriptions);
  };
}

export default new RemoteMicManager();
